"use client";

import Link from "next/link";
import { acceptAll, declineAll, useConsent } from "./consent-store";
import type { CookieConsentSettings } from "./CookieConsent";

/**
 * Fixed cookie-consent banner (LGPD Art. 7/8). Appears for visitors who have
 * not decided yet, or whose stored decision is for an older policy version
 * (forced re-consent). Renders nothing until the client store has hydrated.
 */
export function CookieBanner({
  settings,
  onCustomize,
}: {
  settings: CookieConsentSettings;
  onCustomize: () => void;
}) {
  const { hasDecided, policyVersion: stored, hydrated } = useConsent();

  const needsReconsent = hydrated && hasDecided && stored !== settings.policyVersion;
  const show = hydrated && settings.bannerEnabled && (!hasDecided || needsReconsent);
  if (!show) return null;

  return (
    <div
      className={`lgpd-banner lgpd-banner--${settings.bannerPosition}`}
      role="dialog"
      aria-modal="false"
      aria-label="Aviso de cookies"
    >
      <div className="lgpd-banner__inner">
        <div className="lgpd-banner__text">
          <span className="lgpd-banner__icon" aria-hidden>
            <svg viewBox="0 0 24 24" width="22" height="22">
              <path
                fill="currentColor"
                d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-4-4 4 4 0 0 1-4-4 2 2 0 0 0-2-2Zm-3 7a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm8 5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm-8 3a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z"
              />
            </svg>
          </span>
          <div>
            <strong>{settings.bannerTitle}</strong>
            <p>
              {settings.bannerText}{" "}
              <Link href="/cookies">Política de Cookies</Link> ·{" "}
              <Link href="/privacidade">Política de Privacidade</Link>.
            </p>
          </div>
        </div>
        <div className="lgpd-banner__actions">
          <button
            type="button"
            className="lgpd-btn lgpd-btn--ghost"
            onClick={() => declineAll(settings.policyVersion, "banner")}
          >
            Recusar
          </button>
          <button type="button" className="lgpd-btn lgpd-btn--ghost" onClick={onCustomize}>
            Personalizar
          </button>
          <button
            type="button"
            className="lgpd-btn lgpd-btn--primary"
            onClick={() => acceptAll(settings.policyVersion, "banner")}
          >
            Aceitar tudo
          </button>
        </div>
      </div>
    </div>
  );
}
